import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Wrench, AlertTriangle, Receipt, Home, MapPin, Bell, BellRing } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ShareCard from "@/components/ShareCard";

const PLACEHOLDER = "https://images.pexels.com/photos/31656143/pexels-photo-31656143.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function TenantDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ properties: [], leases: [], rents: [], maint: [], incidents: [], notifications: [] });

  const load = () => Promise.all([
    api.get("/properties"), api.get("/leases"), api.get("/rents"), api.get("/maintenance"), api.get("/incidents"), api.get("/notifications")
  ]).then(([p, l, r, m, i, n]) => setData({ properties: p.data, leases: l.data, rents: r.data, maint: m.data, incidents: i.data, notifications: n.data }));

  useEffect(() => { load(); }, []);

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const lease = data.leases[0];
  const property = data.properties[0];
  const unpaidCount = data.rents.filter(r => r.status !== "paid").length;
  const openMaint = data.maint.filter(m => m.status === "open" || m.status === "in_progress").length;
  const unread = data.notifications.filter(n => !n.is_read);

  return (
    <AppLayout title={`Hi, ${user?.name?.split(" ")[0]}`} subtitle="Your home at a glance">
      {unread.length > 0 && (
        <Card className="p-5 mb-6 border-amber-200 bg-amber-50/60" data-testid="notifications-banner">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-amber-200/60 flex items-center justify-center shrink-0">
              <BellRing className="h-5 w-5 text-amber-800" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-sm">{unread.length} new notification{unread.length !== 1 && "s"}</div>
              <ul className="mt-2 space-y-1.5">
                {unread.slice(0, 3).map(n => (
                  <li key={n.id} className="text-xs text-slate-700" data-testid={`notification-${n.id}`}>
                    <span className="font-medium">{n.title}</span> — {n.body}
                  </li>
                ))}
              </ul>
            </div>
            <Button data-testid="mark-read-button" onClick={markAllRead} size="sm" variant="ghost" className="text-xs">
              <Bell className="h-3.5 w-3.5 mr-1" /> Mark all read
            </Button>
          </div>
        </Card>
      )}

      {!lease ? (
        <Card className="p-12 text-center border-dashed border-2 border-slate-200 mb-6">
          <Home className="h-8 w-8 mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500 mb-1">No active lease found</div>
          <p className="text-xs text-slate-400">Ask your landlord to assign you a property.</p>
        </Card>
      ) : (
        <>
          <Card className="overflow-hidden border-slate-200 mb-6" data-testid="tenant-home-card">
            <div className="grid grid-cols-1 md:grid-cols-3">
              <div className="aspect-video md:aspect-auto bg-slate-100">
                <img src={property?.image_url || PLACEHOLDER} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="md:col-span-2 p-6 space-y-3">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Your Home</div>
                <h2 className="font-display text-2xl font-semibold tracking-tight">{property?.name || "—"}</h2>
                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                  <MapPin className="h-4 w-4" /> {property?.address}
                </div>
                <div className="flex gap-6 pt-3 border-t border-slate-100">
                  <div><div className="text-xs text-slate-500">Monthly rent</div><div className="font-mono-num text-lg font-semibold">${lease.monthly_rent}</div></div>
                  <div><div className="text-xs text-slate-500">Lease start</div><div className="text-sm font-medium">{lease.start_date}</div></div>
                  <div><div className="text-xs text-slate-500">Lease end</div><div className="text-sm font-medium">{lease.end_date || "—"}</div></div>
                </div>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <StatCard icon={Receipt} label="Unpaid rent" value={unpaidCount} hint="months pending" testid="tenant-stat-rent" />
            <StatCard icon={Wrench} label="Active maintenance" value={openMaint} hint="open requests" testid="tenant-stat-maint" />
            <StatCard icon={AlertTriangle} label="Incidents reported" value={data.incidents.length} hint="on record" testid="tenant-stat-incidents" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-6 border-slate-200">
              <h3 className="font-display font-semibold mb-3">Need something fixed?</h3>
              <p className="text-sm text-slate-600 mb-4">Submit a new maintenance request — your landlord will see it instantly.</p>
              <Button data-testid="tenant-new-maint-btn" onClick={() => nav("/tenant/maintenance")} className="bg-slate-900 hover:bg-slate-800">Open Maintenance</Button>
            </Card>
            <Card className="p-6 border-slate-200">
              <h3 className="font-display font-semibold mb-3">Something happened?</h3>
              <p className="text-sm text-slate-600 mb-4">Record an incident with date, severity and details for your records.</p>
              <Button data-testid="tenant-new-incident-btn" onClick={() => nav("/tenant/incidents")} variant="outline">Report Incident</Button>
            </Card>
          </div>

          <div className="mt-6">
            <ShareCard />
          </div>
        </>
      )}

      {!lease && <ShareCard />}
    </AppLayout>
  );
}

function StatCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <Card className="p-6 border-slate-200" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
          <div className="font-display text-3xl font-semibold mt-2 font-mono-num">{value}</div>
          <div className="text-xs text-slate-500 mt-1">{hint}</div>
        </div>
        <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center"><Icon className="h-5 w-5 text-slate-700" /></div>
      </div>
    </Card>
  );
}
